import React from 'react';
import { useMsal } from '@azure/msal-react';
import { motion } from 'framer-motion';
import {
    ArrowRightIcon,
    ShieldCheckIcon,
    CircleStackIcon,
    AcademicCapIcon,
    CloudArrowUpIcon,
    StarIcon
} from '@heroicons/react/24/outline';
import { loginRequest } from '../../config/authConfig';

const Welcome: React.FC = () => {
    const { instance } = useMsal();

    const handleLogin = () => {
        instance.loginRedirect(loginRequest).catch((e) => {
            console.error(e);
        });
    };

    const features = [
        {
            name: 'SAP Gold Partner',
            description: 'Leveraging deep expertise as an SAP Gold Partner specialized in HR digitalization and SuccessFactors.',
            icon: StarIcon,
        },
        {
            name: 'Secure Implementation',
            description: 'Enterprise-grade security for your HR data migration, fully integrated with Microsoft Entra ID.',
            icon: ShieldCheckIcon,
        },
        {
            name: '2S Custom Apps',
            description: 'Extend functionality with our proprietary applications designed to optimize your HR processes.',
            icon: CircleStackIcon,
        },
        {
            name: 'Expert Guidance',
            description: 'From pre-implementation analysis to long-term support, we ensure your HR system works for you.',
            icon: AcademicCapIcon,
        },
    ];

    return (
        <div className="min-h-screen bg-white overflow-hidden">
            {/* Hero Section */}
            <div className="relative pt-16 pb-32 flex content-center items-center justify-center min-h-[75vh]">
                <div className="absolute top-0 w-full h-full bg-center bg-cover" style={{
                    backgroundImage: 'url("https://images.unsplash.com/photo-1557804506-669a67965ba0?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=crop&w=1267&q=80")'
                }}>
                    <span id="blackOverlay" className="w-full h-full absolute opacity-75 bg-brand-navy"></span>
                </div>
                <div className="container relative mx-auto">
                    <div className="items-center flex flex-wrap">
                        <div className="w-full lg:w-8/12 px-4 ml-auto mr-auto text-center">
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8 }}
                            >
                                <div className="inline-flex items-center justify-center px-4 py-2 mb-6 rounded-full bg-brand-red/10 border border-brand-red/20">
                                    <span className="text-brand-red text-sm font-bold uppercase tracking-wider">SAP Gold Partner</span>
                                </div>
                                <h1 className="text-white font-semibold text-5xl font-heading">
                                    Success Solutions Migration Portal
                                </h1>
                                <p className="mt-4 text-xl text-gray-300 max-w-2xl mx-auto">
                                    Don't be alone in the digital transformation of your HR processes. We guide you through every step to ensure you have a great HR system and a good night's sleep.
                                </p>
                                <div className="mt-12">
                                    <button
                                        onClick={handleLogin}
                                        className="bg-brand-red text-white active:bg-accent-600 text-lg font-bold uppercase px-8 py-4 rounded-xl shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150 inline-flex items-center group"
                                    >
                                        Access Migration Portal
                                        <ArrowRightIcon className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                                    </button>
                                </div>
                            </motion.div>
                        </div>
                    </div>
                </div>
                <div
                    className="top-auto bottom-0 left-0 right-0 w-full absolute pointer-events-none overflow-hidden h-70-px"
                    style={{ transform: "translateZ(0px)" }}
                >
                    <svg
                        className="absolute bottom-0 overflow-hidden"
                        xmlns="http://www.w3.org/2000/svg"
                        preserveAspectRatio="none"
                        version="1.1"
                        viewBox="0 0 2560 100"
                        x="0"
                        y="0"
                    >
                        <polygon
                            className="text-gray-50 fill-current"
                            points="2560 0 2560 100 0 100"
                        ></polygon>
                    </svg>
                </div>
            </div>

            {/* Features Section */}
            <section className="pb-20 bg-gray-50 -mt-24">
                <div className="container mx-auto px-4">
                    <div className="flex flex-wrap">
                        {features.map((feature, index) => (
                            <div key={feature.name} className="lg:pt-12 pt-6 w-full md:w-3/12 px-4 text-center">
                                <motion.div
                                    initial={{ opacity: 0, y: 40 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.5, delay: index * 0.1 }}
                                    className="relative flex flex-col min-w-0 break-words bg-white w-full h-full mb-8 shadow-soft rounded-2xl border border-gray-100 p-8 hover:shadow-soft-lg transition-all duration-300"
                                >
                                    <div className="px-4 py-5 flex-auto">
                                        <div className={`text-white p-3 text-center inline-flex items-center justify-center w-12 h-12 mb-5 shadow-lg rounded-xl ${index % 2 === 0 ? 'bg-brand-navy' : 'bg-brand-red'}`}>
                                            <feature.icon className="w-6 h-6" />
                                        </div>
                                        <h6 className="text-xl font-bold font-heading">{feature.name}</h6>
                                        <p className="mt-2 mb-4 text-gray-500 text-sm">
                                            {feature.description}
                                        </p>
                                    </div>
                                </motion.div>
                            </div>
                        ))}
                    </div>

                    <div className="flex flex-wrap items-center mt-32">
                        <div className="w-full md:w-5/12 px-4 mr-auto ml-auto">
                            <div className="text-brand-red p-3 text-center inline-flex items-center justify-center w-16 h-16 mb-6 shadow-lg rounded-full bg-brand-red/10">
                                <CloudArrowUpIcon className="w-8 h-8" />
                            </div>
                            <h3 className="text-3xl mb-2 font-semibold leading-normal font-heading">
                                Trusted by Enterprise Leaders
                            </h3>
                            <p className="text-lg font-light leading-relaxed mt-4 mb-4 text-gray-600">
                                From <strong>ŠKODA AUTO</strong> to <strong>Mattoni 1873</strong> and <strong>LINET Group</strong>, we help leading organizations navigate the complexities of SAP SuccessFactors implementation and data migration.
                            </p>
                            <p className="text-lg font-light leading-relaxed mt-0 mb-4 text-gray-600">
                                Our senior consultants bring technical know-how and deep HR process understanding to every project, ensuring your transition to the cloud is smooth and efficient.
                            </p>
                            <a
                                href="https://www.success-solutions.com/reference"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="font-bold text-brand-navy mt-8 hover:text-brand-red transition-colors inline-flex items-center group"
                            >
                                View our references
                                <ArrowRightIcon className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                            </a>
                        </div>

                        <div className="w-full md:w-4/12 px-4 mr-auto ml-auto">
                            <div className="relative flex flex-col min-w-0 break-words bg-brand-navy w-full mb-6 shadow-lg rounded-3xl p-2">
                                <img
                                    alt="Success Solutions Workshop"
                                    src="https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?ixlib=rb-1.2.1&auto=format&fit=crop&w=700&q=80"
                                    className="w-full align-middle rounded-2xl"
                                />
                                <blockquote className="relative p-8 mb-4">
                                    <svg
                                        preserveAspectRatio="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 583 95"
                                        className="absolute left-0 w-full block h-95-px -top-[94px]"
                                    >
                                        <polygon
                                            points="-30,95 583,95 583,65"
                                            className="text-brand-navy fill-current"
                                        ></polygon>
                                    </svg>
                                    <h4 className="text-xl font-bold text-white font-heading text-center">
                                        Your Guide to Digital HR
                                    </h4>
                                    <p className="text-md font-light mt-2 text-white/80 text-center italic">
                                        "Senior consultants and experts from Success Solutions possess the necessary know-how and technical knowledge to adapt the platform to our needs."
                                    </p>
                                </blockquote>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <footer className="relative bg-gray-50 pt-8 pb-6 border-t border-gray-200">
                <div className="container mx-auto px-4">
                    <div className="flex flex-wrap items-center md:justify-between justify-center">
                        <div className="w-full md:w-4/12 px-4 mx-auto text-center">
                            <div className="text-sm text-gray-500 font-semibold py-1">
                                © {new Date().getFullYear()} Success Solutions. All rights reserved.
                            </div>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default Welcome;
